// ────────────────────────────────────────────────────────────────────────────
// Payment terms: resolution, wording, and due-date arithmetic.
//
// Terms are stored in two places by design. The organisation carries the house
// terms (`defaultPaymentTermDays` / `defaultPaymentTermBasis`); a customer
// carries its own only when it has negotiated them, and `null` there means
// "inherit". Nothing copies the house terms onto customers, so changing them
// moves every customer that never negotiated instead of leaving stale copies.
//
// The day count alone is not enough: "30 days end of month" falls due weeks
// after "30 days from invoice date", and both are ordinary. Hence the basis.
//
// This is the single implementation for the app — the invoice builder uses it
// to default the due date and to derive the wording it snapshots onto the
// invoice, and both settings screens use it to show what the rule means.
// ────────────────────────────────────────────────────────────────────────────
import type {
  Organisation,
  Party,
  PaymentMethodValue,
  PaymentTermBasisValue,
} from '@my/directory-api';

export interface PaymentTermsRule {
  days: number;
  basis: PaymentTermBasisValue;
}

export interface ResolvedPaymentTerms extends PaymentTermsRule {
  /** True when these came from the organisation rather than the customer. */
  inherited: boolean;
  /**
   * Fixed wording that replaces the day-count phrasing, set when the billing
   * treatment decides the terms rather than a negotiated period.
   */
  wording?: string;
}

/**
 * Billing treatments with no credit period, and what the document should say.
 *
 * Prepay is settled before or on despatch, so by the time a document exists the
 * money is normally in - "30 days from invoice date" would be wrong on the page
 * AND wrong in the ledger. An internal movement is never charged at all.
 *
 * Handled here rather than in the document layouts, deliberately: the wording is
 * snapshotted onto `Invoice.PaymentTerms` and the SAME rule computes
 * `Invoice.DueDate`. Overriding the text in a layout would leave the stored
 * terms and the due date saying thirty days, so aged debt would show a cash sale
 * as not yet due for a month - and the fix would have to be repeated in every
 * layout, including per-customer ones.
 */
const IMMEDIATE_TERMS: Partial<Record<PaymentMethodValue, string>> = {
  Cash: 'Payment in advance',
  Internal: 'Internal transfer – no payment due',
};

/** Used when neither the customer nor the organisation has anything set. */
export const FALLBACK_TERMS: PaymentTermsRule = { days: 30, basis: 'InvoiceDate' };

export const PAYMENT_TERM_BASIS_LABEL: Record<PaymentTermBasisValue, string> = {
  InvoiceDate: 'from invoice date',
  EndOfMonth: 'from end of month',
};

/**
 * The terms that actually apply to a customer. A customer overrides only when
 * it has a day count of its own; the basis follows the same source, so a
 * half-set customer (days without basis) still resolves sensibly.
 */
export function resolvePaymentTerms(
  party:
    | Pick<Party, 'paymentTermDays' | 'paymentTermBasis'>
    | Pick<Party, 'paymentTermDays' | 'paymentTermBasis' | 'paymentMethod'>
    | null
    | undefined,
  organisation:
    | Pick<Organisation, 'defaultPaymentTermDays' | 'defaultPaymentTermBasis'>
    | null
    | undefined,
): ResolvedPaymentTerms {
  // The billing treatment wins over any stored period: a customer switched to
  // prepay may still carry the terms it had on credit, and those must not come
  // back out at invoicing time.
  const method = party && 'paymentMethod' in party ? party.paymentMethod : undefined;
  const fixed = method ? IMMEDIATE_TERMS[method] : undefined;
  if (fixed) {
    return { days: 0, basis: 'InvoiceDate', inherited: false, wording: fixed };
  }

  if (party?.paymentTermDays !== null && party?.paymentTermDays !== undefined) {
    return {
      days: party.paymentTermDays,
      basis: party.paymentTermBasis ?? FALLBACK_TERMS.basis,
      inherited: false,
    };
  }
  return {
    days: organisation?.defaultPaymentTermDays ?? FALLBACK_TERMS.days,
    basis: organisation?.defaultPaymentTermBasis ?? FALLBACK_TERMS.basis,
    inherited: true,
  };
}

/** Wording printed on the document, e.g. "30 days from invoice date". */
export function describePaymentTerms(rule: PaymentTermsRule & { wording?: string }): string {
  // A treatment-driven wording is already the final phrasing.
  if (rule.wording) return rule.wording;
  // Zero days is not "0 days from invoice date" — it is payable now, and every
  // invoice in the wild words it that way.
  if (rule.days <= 0) return 'Due on receipt';
  return `${rule.days} days ${PAYMENT_TERM_BASIS_LABEL[rule.basis]}`;
}

/**
 * Due date for an issue date, as `yyyy-MM-dd`.
 *
 * Arithmetic runs in UTC on the date parts rather than through the local
 * timezone: a `new Date('2026-08-18')` is midnight UTC, so adding days in a
 * behind-UTC zone can land on the previous calendar day. Invoices are dated,
 * not timestamped, and the due date must not wobble by machine.
 */
export function computeDueDate(issueDateIso: string, rule: PaymentTermsRule): string {
  const [y, m, d] = issueDateIso.split('-').map(Number);
  if (!y || !m || !d) return issueDateIso;

  const start =
    rule.basis === 'EndOfMonth'
      ? // Day 0 of the *next* month is the last day of this one.
        new Date(Date.UTC(y, m, 0))
      : new Date(Date.UTC(y, m - 1, d));

  start.setUTCDate(start.getUTCDate() + Math.max(0, rule.days));

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${start.getUTCFullYear()}-${pad(start.getUTCMonth() + 1)}-${pad(start.getUTCDate())}`;
}
